// How long a timed-out mutation is given to land at Shopify before the caller
// hears about it, so a workflow it restarts cannot race the aborted original.
const NTT_MUTATION_SETTLE_MS = 15000;

// [id: string, quantity: int]
// config (optional): forwarded to cartAdd ({ step, snapshot, fetch, cartAdd }).
// The add recovers transient iOS in-app-browser aborts and, on give-up, rejects
// (reporting fetch_failed to PostHog) instead of swallowing the error — so callers
// stop redirecting to /checkout with an empty cart.
//
// It goes through CartAddGuard.cartAdd, NOT fetchWithRetry: /cart/add.js
// INCREMENTS, so blind-retrying an add whose response was lost to an in-app-webview
// abort would silently add the shopper's items twice. cartAdd reconciles against the
// server's cart first and only replays an add it can prove never landed.
// Resolves null when the add landed but its response was lost (alreadyApplied) —
// callers here only need the add to have happened before they move on.
//
// Non-ok <500 responses (e.g. 422 sold-out) still reject: cartAdd deliberately
// hands those back to the caller (definitive, not transient) WITHOUT telemetry,
// and an unchecked one here silently redirected the shopper to /checkout without
// the box (D7 companion).
async function addItemsToCart(items, config) {
  console.log("Adding items to cart:", items);
  const addUrl = `${window.Shopify.routes.root}cart/add.js`;

  if (items.length > 0) {
    // Tracking ids onto the cart before the add (n2t-ad-intel-desk#5). Capped
    // at ~800ms and never rejects; a no-op when the asset isn't loaded.
    await beforeAddToCartAttributes();

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ items: items }),
    };
    const cfg = Object.assign({ step: "add-to-cart" }, config);
    const guardedAdd =
      cfg.cartAdd ||
      (typeof window !== "undefined" && window.CartAddGuard && window.CartAddGuard.cartAdd);

    let data;
    if (guardedAdd) {
      let result;
      try {
        result = await guardedAdd(addUrl, options, cfg);
      } catch (addError) {
        // A timed-out add may still land (mayHaveApplied). Hold the rejection
        // until it has had time to settle, as fetchWithRetry does for a timed-out
        // mutation: every funnel offers an immediate retry that clears and
        // re-adds, and one racing the first POST could double the box.
        const settleMs = cfg.settleMs == null ? NTT_MUTATION_SETTLE_MS : cfg.settleMs;
        if (addError && addError.mayHaveApplied && settleMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, settleMs));
        }
        throw addError;
      }
      data = result.alreadyApplied ? null : result.response;
    } else {
      // The guard asset didn't load. Attempt the add exactly once and surface a
      // failure: losing an add is recoverable by the shopper, double-adding is not.
      //
      // A throw here (the iOS in-app-webview abort) would otherwise escape without
      // telemetry, leaving the entire degraded class invisible in PostHog — the
      // same blindness D7 closed for non-ok responses. Report, then rethrow
      // untouched so the caller still sees the original error. The "unguarded"
      // marker distinguishes a deploy gap (cart-add-guard.js 404ing off the theme,
      // the INV-030 failure mode) from cartAdd's own give-up reasons.
      try {
        data = await fetch(addUrl, options);
      } catch (addError) {
        reportCartFailure(
          "add-to-cart",
          addUrl,
          "cart add failed (unguarded, guard asset absent): " +
            ((addError && addError.message) || addError),
        );
        throw addError;
      }
    }

    // `data === null` is the alreadyApplied recovery — the add provably landed,
    // only its response was lost — so it must NOT be treated as a failure. Every
    // other non-ok result must reject WITH telemetry (D7): cartAdd returns
    // definitive <500s rather than throwing, and the unguarded fallback above
    // only reports throws, so this is the single non-ok gate for both paths.
    if (data !== null && (!data || !data.ok)) {
      const message =
        "cart add failed: HTTP " + (data ? data.status : "no response");
      reportCartFailure("add-to-cart", addUrl, message);
      throw new Error(message);
    }

    await stampPosthogSession();
    return data;
  }
}

// fetch_failed telemetry for non-ok <500 responses, which fetchWithRetry
// resolves without reporting (its own capture only fires on retry exhaustion).
// Same event name/shape as fetchWithRetry's give-up path so the existing
// PostHog dashboards see both failure classes. Must never mask the throw.
function reportCartFailure(step, url, message) {
  try {
    if (
      typeof window !== "undefined" &&
      window.posthog &&
      typeof window.posthog.capture === "function"
    ) {
      window.posthog.capture(
        "fetch_failed",
        {
          url: String(url),
          step: step,
          message: message,
          path: (window.location && window.location.pathname) || "",
        },
        { transport: "sendBeacon", send_instantly: true },
      );
    }
  } catch (telemetryError) {
    if (typeof console !== "undefined" && console.debug) {
      console.debug("fetch_failed capture skipped", telemetryError);
    }
  }
}

function cartAttributesModule() {
  const mod = typeof window !== "undefined" && window.N2TCartAttributes;
  return mod && typeof mod.flush === "function" ? mod : null;
}

// Resolves when `promise` settles or after `ms`, whichever is first. Never rejects.
function settleWithin(promise, ms) {
  let timer;
  const cap = new Promise((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return Promise.race([Promise.resolve(promise).catch(() => {}), cap]).finally(() =>
    clearTimeout(timer),
  );
}

async function beforeAddToCartAttributes() {
  try {
    const mod = cartAttributesModule();
    if (mod && typeof mod.beforeAddToCart === "function") {
      await mod.beforeAddToCart();
    }
  } catch (error) {
    if (typeof console !== "undefined" && console.debug) {
      console.debug("cart attributes pre-add skipped", error);
    }
  }
}

// Stamp the PostHog session id onto the cart as a cart attribute so it rides
// standard Shopify checkout into the order's note_attributes — the opaque,
// PII-free join key for the build-a-box session->order link (PostHog replay
// Option B, #131), matched against PostHog sessions.session_id. No-op when the
// PostHog global isn't ready or has no session id yet.
//
// When assets/n2t-cart-attributes.js is loaded (n2t-ad-intel-desk#5) it owns the
// write and sends the full tracking set (posthog_session_id included), deduped
// against what the cart already holds; the direct write below is the fallback.
// Its flush can hand back a pre-add write that stalled past the pre-add cap
// (flush joins the in-flight request), so the post-add wait is capped too: it
// is best-effort and must never hold the caller's redirect to checkout.
// afterAddToCart() shares the module's cap, so a write the pre-add wait
// already gave up on isn't waited on again; settleWithin() covers a module
// without it.
async function stampPosthogSession() {
  try {
    const mod = cartAttributesModule();
    if (mod) {
      await (typeof mod.afterAddToCart === "function"
        ? mod.afterAddToCart()
        : settleWithin(mod.flush("add_to_cart_after"), 800));
      return;
    }
    const sessionId =
      typeof window !== "undefined" &&
      window.posthog &&
      typeof window.posthog.get_session_id === "function" &&
      window.posthog.get_session_id();
    if (!sessionId) return;
    await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { posthog_session_id: sessionId } }),
    });
  } catch (error) {
    // Best-effort: a missing global or a failed write must never block the add
    // or the checkout redirect that follows it.
    if (typeof console !== "undefined" && console.debug) {
      console.debug("posthog session stamp skipped", error);
    }
  }
}

function getUrlParameter(sParam) {
  var sPageURL = window.location.search.substring(1),
    sURLVariables = sPageURL.split("&"),
    sParameterName,
    i;

  for (i = 0; i < sURLVariables.length; i++) {
    sParameterName = sURLVariables[i].split("=");

    if (sParameterName[0] === sParam) {
      return sParameterName[1] === undefined
        ? true
        : decodeURIComponent(sParameterName[1]);
    }
  }
  return false;
}

// Current cart state for the checkout reconcile. Throws on a non-ok response
// so callers surface an error instead of reconciling against garbage.
async function getCart(config) {
  const cartUrl = `${window.Shopify.routes.root}cart.js`;
  const response = await fetchWithRetry(
    cartUrl,
    { method: "GET", headers: { Accept: "application/json" } },
    Object.assign({ retries: 2, backoffMs: 300, step: "cart-get" }, config),
  );
  if (!response || !response.ok) {
    const message =
      "cart fetch failed: HTTP " + (response ? response.status : "no response");
    reportCartFailure("cart-get", cartUrl, message);
    throw new Error(message);
  }
  return response.json();
}

// Pure selector for the seafood-plan checkout reconcile: which cart lines the
// flow owns and must replace. A line qualifies when it is tagged (`_freebie` /
// `_n2t_flow` line-item property) OR — migration for carts built by pre-hotfix
// code — untagged but a known flow variant id / box product handle. A tagged
// re-add cannot merge with an untagged same-variant line (properties differ →
// separate line), so skipping the untagged sweep would re-introduce the exact
// stacking bug this exists to fix. Top-level + injectable for vitest.
function collectFlowLineKeys(cart, spec) {
  const items = (cart && cart.items) || [];
  const variantIds = new Set(((spec && spec.variantIds) || []).map(String));
  const handles = new Set((spec && spec.productHandles) || []);
  const keys = [];
  for (const item of items) {
    if (!item || !item.key) continue;
    const props = item.properties || {};
    const tagged = props._freebie != null || props._n2t_flow != null;
    const variantId = item.variant_id != null ? item.variant_id : item.id;
    const legacyFlowLine =
      !tagged && (variantIds.has(String(variantId)) || handles.has(item.handle));
    if (tagged || legacyFlowLine) {
      keys.push(item.key);
    }
  }
  return keys;
}

// Removes the flow's lines by line `key` only — with tagged and untagged
// duplicates of the same variant in the cart, a numeric-variant-id call hits
// the wrong line. All keys go in ONE /cart/update.js POST (D7): the old
// per-key /cart/change.js loop was non-atomic — each removal re-keys the
// remaining lines (Shopify rotates keys when discount applications change), so
// a later key from the stale snapshot 400'd mid-sweep and left the cart
// half-swept (box gone, freebie orphaned at full price). One request is
// all-or-nothing, and the updates object de-duplicates identical keys free.
// The response is ok-checked: fetchWithRetry resolves non-ok <500 responses,
// and a swallowed 400 would silently leave stale lines in place (stacking).
async function removeFlowLines(cart, spec, config) {
  const keys = collectFlowLineKeys(cart, spec);
  if (keys.length === 0) return;
  const updates = {};
  for (const key of keys) {
    updates[key] = 0;
  }
  const updateUrl = `${window.Shopify.routes.root}cart/update.js`;
  const response = await fetchWithRetry(
    updateUrl,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates: updates }),
    },
    Object.assign({ retries: 2, backoffMs: 300, step: "cart-line-remove", bodyOptional: true }, config),
  );
  if (!response || !response.ok) {
    const message =
      "cart line remove failed: HTTP " +
      (response ? response.status : "no response");
    reportCartFailure("cart-line-remove", updateUrl, message);
    throw new Error(message);
  }
}

// Pure predicate for the entitlement observer (cart-entitlement.js): does the
// cart contain a line for one of the qualifying plan-box products? Matches on
// `line.product_id` (the AJAX cart.js line shape) — cart lines carry no
// product tags, so `qualifyingProductIds` (rendered server-side from the
// `plan-boxes` collection, see snippets/n2t-entitlement-config.liquid) is the
// tag->theme bridge. Top-level + injectable for vitest.
function cartHasQualifyingLine(cart, qualifyingProductIds) {
  const items = (cart && cart.items) || [];
  const qualifying = new Set((qualifyingProductIds || []).map(String));
  if (qualifying.size === 0) return false;
  return items.some((item) => item && qualifying.has(String(item.product_id)));
}

// Pure decision function for the entitlement observer: given the current cart
// and the rendered entitlement config, decide whether a freebie/flow line (or
// a stale freebie discount_code cookie) should be stripped because the cart
// no longer holds a qualifying plan box.
//
// config = { qualifyingProductIds, freebieCodes, discountCookieValue }
//
// Rules (checked in order):
//   1. Empty qualifyingProductIds -> 'none' (FAIL-SAFE: the `plan-boxes`
//      collection doesn't exist yet, so there's nothing to enforce against —
//      never strip on an empty/misconfigured entitlement list).
//   2. Cart has a qualifying line -> 'none' (entitled).
//   3. A line carries `_freebie` / `_n2t_flow` (tagged flow line), OR the
//      cookie holds a known freebie code -> 'strip'.
//   4. Otherwise -> 'none' (nothing to reconcile; e.g. an unrelated/affiliate
//      cookie value, which must NEVER be touched).
//
// Top-level + injectable for vitest.
function decideEntitlementAction(cart, config) {
  const cfg = config || {};
  const qualifyingProductIds = cfg.qualifyingProductIds || [];
  const freebieCodes = cfg.freebieCodes || [];

  if (qualifyingProductIds.length === 0) {
    return { action: 'none', reason: 'no-qualifying-products-configured' };
  }

  if (cartHasQualifyingLine(cart, qualifyingProductIds)) {
    return { action: 'none', reason: 'qualifying-line-present' };
  }

  const items = (cart && cart.items) || [];
  const hasTaggedFlowLine = items.some((item) => {
    const props = (item && item.properties) || {};
    return props._freebie != null || props._n2t_flow != null;
  });
  if (hasTaggedFlowLine) {
    return { action: 'strip', reason: 'orphan-tagged-flow-line' };
  }

  const cookieValue = cfg.discountCookieValue;
  if (cookieValue && freebieCodes.includes(cookieValue)) {
    return { action: 'strip', reason: 'orphan-freebie-cookie' };
  }

  return { action: 'none', reason: 'no-mismatch' };
}

// Subscription offer lines (Black Friday 2026: a $0 "free sockeye for a year" product
// that requires a selling plan). Build a Box v2 tags them `_n2t_offer`; the handle
// catches one that reached the cart some other way. They are not freebie/flow lines,
// so decideEntitlementAction never strips them.
const OFFER_LINE_HANDLES = ["free-sockeye-galley-cuts-1-lb"];

function isOfferLine(item) {
  if (!item) return false;
  const props = item.properties || {};
  if (props._n2t_offer != null && props._n2t_offer !== "") return true;
  return OFFER_LINE_HANDLES.includes(item.handle);
}

// Pure: keys of offer lines with no subscription box to ride along with. A box line is
// a subscription line (selling_plan_allocation) from the custom box (`_dynamicBoxId`)
// or a qualifying plan-box product. One-time boxes never qualify.
// Top-level + injectable for vitest.
function orphanOfferLineKeys(cart, qualifyingProductIds) {
  const items = (cart && cart.items) || [];
  const offers = items.filter(isOfferLine);
  if (offers.length === 0) return [];
  const qualifying = new Set((qualifyingProductIds || []).map(String));
  const hasSubscriptionBox = items.some((item) => {
    if (!item || isOfferLine(item) || !item.selling_plan_allocation) return false;
    const props = item.properties || {};
    return props._dynamicBoxId != null || qualifying.has(String(item.product_id));
  });
  if (hasSubscriptionBox) return [];
  return offers.map((item) => item.key).filter(Boolean);
}

// Pure: the cart/update.js `updates` map that brings offer lines back in line, or null
// when nothing needs changing. Orphans go to 0; otherwise the cart keeps one offer line
// at quantity 1 (a shopper can't raise it, and a stray second line is removed).
// Top-level + injectable for vitest.
function offerLineUpdates(cart, qualifyingProductIds) {
  const items = (cart && cart.items) || [];
  const updates = {};
  const orphans = orphanOfferLineKeys(cart, qualifyingProductIds);
  if (orphans.length > 0) {
    orphans.forEach((key) => {
      updates[key] = 0;
    });
    return updates;
  }
  const offers = items.filter((item) => isOfferLine(item) && item.key);
  offers.forEach((item, i) => {
    const want = i === 0 ? 1 : 0;
    if (Number(item.quantity) !== want) updates[item.key] = want;
  });
  return Object.keys(updates).length > 0 ? updates : null;
}

// "Every 6 Weeks", "Every Month 15% off", "Monthly" -> "6-week", "1-month", "1-month".
// Plan ids differ between the offer's own Subscribe & Save group and every other group,
// so the offer is matched to a subscription by delivery interval in the plan name.
function offerIntervalKey(text) {
  const m = /(\d+)?\s*(day|week|month|year)(?:s|ly)?\b/i.exec(String(text || ""));
  return m ? (m[1] || "1") + "-" + m[2].toLowerCase() : null;
}

function linePlanName(item) {
  const alloc = item && item.selling_plan_allocation;
  return (alloc && alloc.selling_plan && alloc.selling_plan.name) || "";
}

function linePlanId(item) {
  const alloc = item && item.selling_plan_allocation;
  return alloc && alloc.selling_plan && alloc.selling_plan.id != null ? String(alloc.selling_plan.id) : "";
}

// Pure: what the cart needs so the free sockeye follows the Black Friday rules (Luke +
// Ryan, 2026-10-27). One free offer line, quantity 1, on the offer plan with the same
// interval as a qualifying subscription. A qualifying subscription is the subscription
// lines on one interval that the offer has a plan for (Freezer Fills' 4- and 6-month
// plans qualify since those offer plans were added 2026-11-01; a schedule with no offer
// plan never does), worth at least offer.minCents per shipment, and not from the
// Seafood Plan page (`_n2t_flow: seafood-plan`, which gets the free scallops instead:
// any Seafood Plan line in the cart means no sockeye at all). One-time lines never count.
// With offer.sourceFlows set, the cart must also come from the offer (Ryan, 2026-11-01):
// a subscription line tagged `_n2t_flow` with one of those flows (the Black Friday page)
// or `_n2t_offer_source` (Build a Box). Only paid subscription lines count: never the
// free line (so the mark survives the sockeye coming out) and never the one-time insert
// the Black Friday page tags too. Otherwise no sockeye.
// Returns { updates, add }: a cart/update.js map (or null) and one line to add (or null).
// Top-level + injectable for vitest.
function planOfferChanges(cart, offer) {
  const items = (cart && cart.items) || [];
  const offers = items.filter((item) => isOfferLine(item) && item.key);
  const offerPlans = (offer && offer.plans) || [];
  const planForInterval = {};
  offerPlans.forEach((p) => {
    const k = offerIntervalKey(p.name);
    if (k && !planForInterval[k]) planForInterval[k] = String(p.id);
  });

  const seafoodPlan = items.some((item) => {
    const props = (item && item.properties) || {};
    return props._n2t_flow === "seafood-plan";
  });

  const sourceFlows = Array.isArray(offer && offer.sourceFlows) ? offer.sourceFlows : null;
  const fromOffer =
    !sourceFlows ||
    items.some((item) => {
      if (!item || isOfferLine(item) || !item.selling_plan_allocation) return false;
      const props = item.properties || {};
      return sourceFlows.indexOf(props._n2t_flow) !== -1 || (props._n2t_offer_source != null && props._n2t_offer_source !== "");
    });

  let target = null;
  if (!seafoodPlan && fromOffer) {
    const totals = {};
    items.forEach((item) => {
      if (!item || isOfferLine(item) || !item.selling_plan_allocation) return;
      const k = offerIntervalKey(linePlanName(item));
      if (!k || !planForInterval[k]) return;
      totals[k] = (totals[k] || 0) + Number(item.final_line_price != null ? item.final_line_price : item.line_price || 0);
    });
    let best = null;
    Object.keys(totals).forEach((k) => {
      if (totals[k] >= Number((offer && offer.minCents) || 0) && (!best || totals[k] > totals[best])) best = k;
    });
    if (best) target = planForInterval[best];
  }

  const updates = {};
  let keep = null;
  if (target) keep = offers.find((item) => linePlanId(item) === target) || null;
  offers.forEach((item) => {
    const want = item === keep ? 1 : 0;
    if (Number(item.quantity) !== want) updates[item.key] = want;
  });
  const add =
    target && !keep && offer && offer.variantId
      ? {
          id: Number(offer.variantId),
          quantity: 1,
          selling_plan: Number(target),
          properties: { _n2t_offer: offer.key || "offer" },
        }
      : null;
  return { updates: Object.keys(updates).length > 0 ? updates : null, add: add };
}

// Removes every offer line from `cart` (Seafood Plan checkout: that page's offer is the
// free scallops, never the sockeye too). Resolves without a request when there are none.
async function removeOfferLines(cart, config) {
  const keys = ((cart && cart.items) || []).filter((item) => isOfferLine(item) && item.key).map((item) => item.key);
  if (keys.length === 0) return null;
  const updates = {};
  keys.forEach((key) => {
    updates[key] = 0;
  });
  const updateUrl = `${window.Shopify.routes.root}cart/update.js`;
  const response = await fetchWithRetry(
    updateUrl,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ updates }) },
    Object.assign({ retries: 2, backoffMs: 300, step: "cart-offer-remove", bodyOptional: true }, config),
  );
  // Same contract as removeFlowLines: a non-ok answer stops the checkout instead of
  // letting it continue with the sockeye still in the cart.
  if (!response || !response.ok) {
    const message = "cart offer remove failed: HTTP " + (response ? response.status : "no response");
    reportCartFailure("cart-offer-remove", updateUrl, message);
    throw new Error(message);
  }
  return response;
}

function clearCart(config) {
  return fetchWithRetry(
    `${window.Shopify.routes.root}cart/clear.js`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    },
    Object.assign({ retries: 2, backoffMs: 300, step: "cart-clear", bodyOptional: true }, config),
  );
}
function formatMoney(number, decPlaces, decSep, thouSep) {
  (decPlaces = isNaN((decPlaces = Math.abs(decPlaces))) ? 2 : decPlaces),
    (decSep = typeof decSep === "undefined" ? "." : decSep);
  thouSep = typeof thouSep === "undefined" ? "," : thouSep;
  var sign = number < 0 ? "-" : "";
  var i = String(
    parseInt((number = Math.abs(Number(number) || 0).toFixed(decPlaces)))
  );
  var j = i.length > 3 ? i.length % 3 : 0;

  return (
    sign +
    (j ? i.substr(0, j) + thouSep : "") +
    i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thouSep) +
    (decPlaces
      ? decSep +
        Math.abs(number - i)
          .toFixed(decPlaces)
          .slice(2)
      : "")
  );
}

// Resilience wrapper for the funnel's cart/Skio fetches. iOS Facebook/Instagram
// in-app WebViews abort in-flight requests when the app backgrounds them, which
// silently dead-ends add-to-cart on the build-a-box path (PostHog
// 'TypeError: Load failed'). This retries transient failures and reports a
// give-up to PostHog so we can see exactly which endpoint aborted.
//
// Each attempt is also bounded (cfg.timeoutMs, default 15s; 0 disables). The
// same webviews sometimes leave a request pending forever instead of rejecting,
// and an await that never settles never reaches the caller's error path, so
// every CTA it disabled stays disabled until reload (#533). A timed-out attempt
// is aborted and treated like any other transient failure.
const NTT_FETCH_TIMEOUT_MS = 15000;

// Reads the body once; the response's json() and text() then resolve from the
// buffered copy.
async function nttBufferResponse(response) {
  if (!response || typeof response !== 'object') return response;
  let body;
  let parsed;
  if (typeof response.text === 'function') {
    body = await response.text();
  } else if (typeof response.json === 'function') {
    parsed = await response.json();
    body = JSON.stringify(parsed);
  } else {
    return response;
  }
  const text = async () => body;
  const json = async () => (parsed !== undefined ? parsed : JSON.parse(body));
  try {
    // Same object back, so callers keep its identity, headers and status.
    response.text = text;
    response.json = json;
    return response;
  } catch (error) {
    return { ok: response.ok, status: response.status, headers: response.headers, text, json };
  }
}

function fetchWithTimeout(url, options, timeoutMs, bodyOptional) {
  // A timeout we cannot enforce with an abort would leave the request live
  // while the caller moves on (and may replay a mutation), so without
  // AbortController the request stays unbounded, as it was before.
  if (!(timeoutMs > 0) || typeof AbortController !== 'function') return fetch(url, options);

  const controller = new AbortController();
  const callerSignal = options && options.signal;
  let forwardAbort = null;
  // Keep the caller's own abort working. AbortSignal.any is too new for the
  // iOS versions we still serve, so forward it by hand.
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort();
    else {
      forwardAbort = () => controller.abort();
      callerSignal.addEventListener('abort', forwardAbort, { once: true });
    }
  }
  const requestOptions = Object.assign({}, options, { signal: controller.signal });

  const isRead = /^(GET|HEAD)$/i.test((options && options.method) || 'GET');
  let okMutation = null;
  let timer;
  const deadline = new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      const error = new Error('Request timed out after ' + timeoutMs + 'ms');
      error.name = 'TimeoutError';
      if (okMutation && bodyOptional) {
        // The mutation already succeeded; only its body stalled. Hand back the
        // success to a caller that never reads the body. One that does (it
        // verifies the reply) gets the timeout, so its retry loop runs.
        okMutation.text = () => Promise.reject(error);
        okMutation.json = () => Promise.reject(error);
        resolve(okMutation);
      } else {
        // A body that stalled after 2xx headers is not an ambiguous request:
        // Shopify answered, so fetchWithRetry may retry it (every caller that
        // reads the body sends an idempotent update). Only a request with no
        // answer at all may still be landing.
        if (okMutation) error.committed = true;
        // Reject before aborting: the abort rejects the request synchronously,
        // and the race must report the timeout, not a generic AbortError.
        reject(error);
      }
      controller.abort();
    }, timeoutMs);
  });

  // The deadline covers the body too: headers can arrive and the body still
  // stall. Reading it here keeps a body timeout inside the caller's own error
  // handling (retry, reconcile, telemetry) instead of surfacing later.
  const request = Promise.resolve(fetch(url, requestOptions)).then((response) => {
    if (!isRead && response && response.ok) okMutation = response;
    return nttBufferResponse(response).catch((bodyError) => {
      // As with a stalled body: a 2xx mutation already applied, so a body that
      // fails to read (an in-app webview's "Load failed") must not send it round
      // the retry loop. Only for callers that never read the body (bodyOptional):
      // the rest parse it to verify the result, and their idempotent request is
      // safer retried than handed back unreadable.
      if (!(bodyOptional && okMutation)) throw bodyError;
      const fail = () => Promise.reject(bodyError);
      try {
        response.text = fail;
        response.json = fail;
        return response;
      } catch (assignError) {
        return { ok: response.ok, status: response.status, headers: response.headers, text: fail, json: fail };
      }
    });
  });
  return Promise.race([request, deadline]).finally(() => {
    clearTimeout(timer);
    if (forwardAbort) callerSignal.removeEventListener('abort', forwardAbort);
  });
}

async function fetchWithRetry(url, options, config) {
  const cfg = config || {};
  const retries = cfg.retries == null ? 2 : cfg.retries;
  const backoffMs = cfg.backoffMs == null ? 300 : cfg.backoffMs;
  const timeoutMs = cfg.timeoutMs == null ? NTT_FETCH_TIMEOUT_MS : cfg.timeoutMs;
  const settleMs = cfg.settleMs == null ? NTT_MUTATION_SETTLE_MS : cfg.settleMs;
  let mutationTimedOut = false;

  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options, timeoutMs, cfg.bodyOptional === true);
      if (response && response.ok) {
        return response;
      }
      if (!response || response.status < 500) {
        // Definitive answer (e.g. 422 sold-out): not transient, hand it back so
        // the caller can read the body and message the shopper.
        return response;
      }
      // 5xx: transient server error — fall through to retry like an abort.
      lastError = new Error('HTTP ' + response.status);
    } catch (error) {
      lastError = error;
      // Aborting stops the browser waiting, not a request Shopify already has: a
      // timed-out mutation can still land late, so replaying it (or letting the
      // caller's workflow carry on) could race the original. Reads are safe.
      if (
        error &&
        error.name === 'TimeoutError' &&
        !error.committed &&
        !/^(GET|HEAD)$/i.test((options && options.method) || 'GET')
      ) {
        error.mayHaveApplied = true;
        mutationTimedOut = true;
        break;
      }
    }
    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, backoffMs * (attempt + 1)));
    }
  }

  // Out of retries: report which endpoint/step aborted so we can see it in
  // PostHog, then re-throw so the caller can surface an error instead of a
  // silent dead-end. Telemetry must never mask the original failure.
  try {
    if (
      typeof window !== 'undefined' &&
      window.posthog &&
      typeof window.posthog.capture === 'function'
    ) {
      // Deliver over sendBeacon and force it out of the batch queue: the abort
      // that killed the fetch is the app tearing the page down, which also
      // cancels PostHog's default async request. sendBeacon is the transport the
      // browser must still flush on unload; send_instantly skips the batch so it
      // leaves before the page is gone. Without this the event dies ~100% of the
      // time it matters and fetch_failed reads as ~0 despite real lost sales (#112).
      window.posthog.capture(
        'fetch_failed',
        {
          url: String(url),
          step: cfg.step || '',
          message: (lastError && lastError.message) || String(lastError),
          path: (window.location && window.location.pathname) || '',
        },
        { transport: 'sendBeacon', send_instantly: true }
      );
    }
  } catch (telemetryError) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('fetch_failed capture skipped', telemetryError);
    }
  }
  // Report first (above), then hold the rejection so the caller cannot
  // restart its workflow while the aborted mutation may still be landing.
  if (mutationTimedOut && settleMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, settleMs));
  }
  throw lastError;
}

// Visible, retryable error surface for a failed add-to-cart. When fetchWithRetry
// gives up on an iOS in-app-browser abort, addItemsToCart rejects and the caller
// no longer redirects to an empty /checkout — but without this the shopper is
// left on a dead button. showCartError renders an accessible alert with a Retry
// action so the funnel recovers instead of silently dead-ending.
function showCartError(message, options) {
  const opts = options || {};

  // Singleton: replace any prior toast so repeated failures don't stack.
  const prior = document.getElementById('ntt-cart-error');
  if (prior) prior.remove();

  const el = document.createElement('div');
  el.id = 'ntt-cart-error';
  el.className = 'ntt-cart-error';
  el.setAttribute('role', 'alert');
  el.setAttribute('aria-live', 'assertive');
  // Critical styles are inline so the toast works on every funnel page without a
  // stylesheet dependency and outranks overlay UIs — notably the Klaviyo popup
  // at z-index 90000 that otherwise sits on top on the build-a-box path.
  el.style.cssText = [
    'position:fixed',
    'left:50%',
    'bottom:24px',
    'transform:translateX(-50%)',
    'z-index:2147483000',
    'max-width:min(440px,calc(100vw - 32px))',
    'display:flex',
    'align-items:center',
    'gap:12px',
    'padding:14px 16px',
    'background:#2B2C2D',
    'color:#fff',
    'border-radius:8px',
    'box-shadow:0 6px 24px rgba(0,0,0,0.28)',
    'font-size:15px',
    'line-height:1.35',
  ].join(';');

  const text = document.createElement('span');
  text.className = 'ntt-cart-error__message';
  text.textContent = message;
  text.style.cssText = 'flex:1 1 auto';
  el.appendChild(text);

  if (typeof opts.onRetry === 'function') {
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'ntt-cart-error__retry';
    retry.textContent = 'Try again';
    retry.style.cssText = [
      'flex:0 0 auto',
      'cursor:pointer',
      'border:0',
      'border-radius:6px',
      'padding:9px 14px',
      'background:#fff',
      'color:#2B2C2D',
      'font-weight:600',
      'font-size:14px',
    ].join(';');
    retry.addEventListener('click', function () {
      el.remove();
      opts.onRetry();
    });
    el.appendChild(retry);
  }

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'ntt-cart-error__dismiss';
  dismiss.setAttribute('aria-label', 'Dismiss');
  dismiss.textContent = '✕';
  dismiss.style.cssText = [
    'flex:0 0 auto',
    'cursor:pointer',
    'border:0',
    'background:transparent',
    'color:#fff',
    'opacity:0.8',
    'font-size:16px',
    'line-height:1',
    'padding:4px',
  ].join(';');
  dismiss.addEventListener('click', function () {
    el.remove();
  });
  el.appendChild(dismiss);

  document.body.appendChild(el);
  return el;
}

// Browser-only: attach the public API to window. Guarded so require() under bare
// Node (no jsdom) doesn't throw on `window`; consistent with the ship-week assets.
if (typeof window !== 'undefined') {
  window.NttUtils = window.NttUtils ?? {};
  window.NttUtils.addItemsToCart = addItemsToCart;
  window.NttUtils.getUrlParameter = getUrlParameter;
  window.NttUtils.formatMoney = formatMoney;
  window.NttUtils.clearCart = clearCart;
  window.NttUtils.getCart = getCart;
  window.NttUtils.collectFlowLineKeys = collectFlowLineKeys;
  window.NttUtils.removeFlowLines = removeFlowLines;
  window.NttUtils.fetchWithRetry = fetchWithRetry;
  window.NttUtils.showCartError = showCartError;
  window.NttUtils.cartHasQualifyingLine = cartHasQualifyingLine;
  window.NttUtils.decideEntitlementAction = decideEntitlementAction;
  window.NttUtils.orphanOfferLineKeys = orphanOfferLineKeys;
  window.NttUtils.offerLineUpdates = offerLineUpdates;
  window.NttUtils.planOfferChanges = planOfferChanges;
  window.NttUtils.removeOfferLines = removeOfferLines;
  window.NttUtils.isOfferLine = isOfferLine;
}

// Node-only test export. Skipped under <script> in the theme (process undefined).
if (
  typeof module === 'object' && module !== null &&
  typeof module.exports === 'object' &&
  typeof process !== 'undefined' && process.versions != null && process.versions.node != null
) {
  module.exports = { formatMoney, getUrlParameter, addItemsToCart, clearCart, getCart, collectFlowLineKeys, removeFlowLines, fetchWithRetry, showCartError, cartHasQualifyingLine, decideEntitlementAction, orphanOfferLineKeys, offerLineUpdates, planOfferChanges, removeOfferLines, isOfferLine };
}
